import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../CustomImageHandler';
import { formatDate } from '../../functions/Functions';
import { IContract } from '../../../model/shared/IContract';

/** Summary of an annex that waits for the influencer / agency, shown above the contract data */
interface IContractAnnexInfoProps {
    contract?: IContract;
    // The description is an input while the amendment is edited
    hidden?: boolean;
    // In the history the header already names the amendment, only the description and the parties are shown
    compact?: boolean;
}

export const ContractAnnexInfo = ({ contract, hidden, compact }: IContractAnnexInfoProps) => {
    const { t } = useTranslation();

    // Waiting for the influencer (pre_annex) or already signed (in effect, or replaced by a later amendment)
    const isPending = contract?.status === 'pre_annex';
    const isSigned = ['accepted', 'completed', 'annexed'].includes(contract?.status ?? '');
    if (!contract?.isAnnex || (!isPending && !isSigned) || hidden) {
        return null;
    }

    const prefix = 'brand.campaign.book.contract.modal.annex';
    const sender = contract.sentBy;
    const fullName = (person?: { firstName?: string; lastName?: string }) =>
        `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim();
    const senderName = fullName(sender);
    const influencer = contract.user;
    const influencerName = fullName(influencer);
    const createdAt = contract.createdAt ?? '';

    return (
        <div className={compact ? 'my-3' : 'mb-3'}>
            {!compact && (
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
                    <p className="text-muted mb-0">
                        {t(`${prefix}.label`)} #{contract.annexNumber} • {t(`${prefix}.to_contract`)} #
                        {contract.annexOf?.id}
                    </p>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <p className={`status ${isPending ? 'pre_annex' : 'completed'} text-dark`}>
                            {t(`${prefix}.label`)} #{contract.annexNumber}
                        </p>
                        <span className="text-muted font-12">
                            {t(`${prefix}.sent_at`)} {formatDate(createdAt)} - {createdAt.substring(11, 16)}
                        </span>
                    </div>
                </div>
            )}
            <div className={`annex-description-card ${isSigned ? 'signed' : ''} mb-3`}>
                <h6 className="mb-1">{t(`${prefix}.annex_description`)}</h6>
                <p className="mb-0">{contract.annexDescription}</p>
            </div>
            <Row>
                <Col md={6} className="mb-3 mb-md-0">
                    <div className="border rounded p-3 d-flex align-items-center justify-content-between h-100">
                        <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <CustomImageHandler
                                thumbnailPath={influencer?.profilePhoto?.thumbnail}
                                photoPath={influencer?.profilePhoto?.thumbnail as string}
                                altTag="User Profile"
                                classes="user-img-rounded"
                                style={{ width: 40, height: 40 }}
                                name={influencerName}
                            />
                            <div>
                                <div className="text-muted text-uppercase font-10">{t(`${prefix}.influencer`)}</div>
                                <strong>{influencerName}</strong>
                            </div>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                            {isPending ? (
                                <>
                                    <p className="status pending text-dark">{t('general.offerStatus.pending')}</p>
                                    <div className="text-muted font-10 mt-1">{t(`${prefix}.awaiting_response`)}</div>
                                </>
                            ) : (
                                <>
                                    <p className="status completed text-dark">{t(`${prefix}.signed`)}</p>
                                    <div className="text-muted font-10 mt-1">
                                        {formatDate(contract.annexAcceptedAt ?? '')}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Col>
                <Col md={6}>
                    <div className="border rounded p-3 d-flex align-items-center justify-content-between h-100">
                        <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <CustomImageHandler
                                thumbnailPath={sender?.profilePhoto?.thumbnail}
                                photoPath={sender?.profilePhoto?.thumbnail as string}
                                altTag="User Profile"
                                classes="user-img-rounded"
                                style={{ width: 40, height: 40 }}
                                name={senderName}
                            />
                            <div>
                                <div className="text-muted text-uppercase font-10">{t(`${prefix}.supplier`)}</div>
                                <strong>{senderName}</strong>
                            </div>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                            <p className="status completed text-dark">{t(`${prefix}.signed`)}</p>
                            <div className="text-muted font-10 mt-1">{formatDate(createdAt)}</div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};
